// src/pages/TicketLookup/components/QrScannerModal.tsx
// Modal đọc mã QR trên vé — dùng chung cho Tra cứu vé, Tạo phiếu giao vé, Tạo phiếu thu hồi vé và Trả
// thưởng (TicketQrScanControl). Cho người dùng CHỌN nguồn đọc mã (giống trang phiên quét QrScanSessionPage):
//   - Camera thiết bị (html5-qrcode, quét liên tục theo khung hình).
//   - Máy quét nối CỔNG COM (USB Serial) — đọc trực tiếp qua Web Serial API (hook useSerialScanner).
//   - Chụp / chọn ảnh vé — giải mã QR từ file ảnh (Html5Qrcode.scanFile).
// Mỗi nguồn đọc được mã đều trả về qua cùng 1 callback onScanned(decodedText) — nơi gọi giữ nguyên.

import React, { useEffect, useRef, useState } from 'react';
import {
  Modal, ModalHeader, ModalBody, Button, Input, FormFeedback, Label, Spinner,
  Dropdown, DropdownToggle, DropdownMenu, DropdownItem,
} from 'reactstrap';
import { Html5Qrcode } from 'html5-qrcode';
import { useSerialScanner } from 'hooks';
import { stopHtml5QrcodeSafely } from 'helpers/html5QrcodeCamera';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('ticket_lookup');

const SCANNER_ELEMENT_ID = 'ticket-lookup-qr-scanner';
// Element ẩn riêng cho việc giải mã ảnh — không dùng chung với element camera đang chạy.
const FILE_READER_ELEMENT_ID = 'ticket-lookup-qr-file-reader';

// Ghi nhớ nguồn quét đã chọn giữa các lần mở modal.
const SOURCE_PREF_KEY = 'qrScannerModal.source';
// Dùng chung khoá baud với trang phiên quét để 2 nơi cùng 1 cấu hình máy quét.
const SERIAL_BAUD_PREF_KEY = 'qrScanSession.serialBaud';
const SERIAL_BAUD_OPTIONS = [9600, 19200, 38400, 57600, 115200];

type ScanSource = 'camera' | 'serial' | 'image';
type ScannerStatus = 'idle' | 'starting' | 'scanning' | 'error';

interface QrScannerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onScanned: (decodedText: string) => void;
}

const readSourcePref = (): ScanSource => {
  try {
    const saved = localStorage.getItem(SOURCE_PREF_KEY);
    if (saved === 'camera' || saved === 'serial' || saved === 'image') return saved;
  } catch {
    /* trình duyệt chặn localStorage — dùng mặc định */
  }
  return 'camera';
};

const readBaudPref = (): number => {
  try {
    const saved = Number(localStorage.getItem(SERIAL_BAUD_PREF_KEY));
    if (SERIAL_BAUD_OPTIONS.includes(saved)) return saved;
  } catch {
    /* bỏ qua */
  }
  return 9600;
};

const QrScannerModal: React.FC<QrScannerModalProps> = ({ isOpen, onClose, onScanned }) => {
  const [source, setSource] = useState<ScanSource>(readSourcePref);
  const [serialBaud, setSerialBaud] = useState<number>(readBaudPref);
  // true khi Modal đã hiện xong (onOpened) — camera chỉ được khởi động sau mốc này.
  const [isShown, setIsShown] = useState(false);
  const [status, setStatus] = useState<ScannerStatus>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [manualLink, setManualLink] = useState('');
  const [manualError, setManualError] = useState('');
  const [isDecodingImage, setIsDecodingImage] = useState(false);
  const [imageError, setImageError] = useState('');
  const [isSourceMenuOpen, setIsSourceMenuOpen] = useState(false);
  // Promise resolve khi camera của lần bật trước đã giải phóng hẳn — lần bật sau chờ mốc này.
  const cameraReleasedRef = useRef<Promise<void>>(Promise.resolve());

  // Giữ bản onScanned mới nhất — camera/cổng COM gọi lại từ callback chạy dài.
  const onScannedRef = useRef(onScanned);
  useEffect(() => { onScannedRef.current = onScanned; }, [onScanned]);

  useEffect(() => {
    try {
      localStorage.setItem(SOURCE_PREF_KEY, source);
    } catch {
      /* bỏ qua */
    }
  }, [source]);

  useEffect(() => {
    try {
      localStorage.setItem(SERIAL_BAUD_PREF_KEY, String(serialBaud));
    } catch {
      /* bỏ qua */
    }
  }, [serialBaud]);

  const serialScanner = useSerialScanner({
    baudRate: serialBaud,
    onScan: (code: string) => onScannedRef.current(code),
    onError: (message) => setErrorMessage(message),
  });

  // Bật camera khi modal đã hiện VÀ đang chọn nguồn camera; đổi nguồn / đóng modal thì tắt.
  // Lần start mới luôn XẾP HÀNG sau lần dừng trước (cameraReleasedRef) — bật/tắt/đổi nguồn liên tục khi
  // trình duyệt còn đang hỏi quyền cũng không bị 2 instance tranh nhau camera / element.
  useEffect(() => {
    if (!isShown || source !== 'camera') return undefined;

    let cancelled = false;
    setStatus('starting');
    setErrorMessage('');
    const html5QrCode = new Html5Qrcode(SCANNER_ELEMENT_ID);

    const startPromise = cameraReleasedRef.current.then(() => {
      if (cancelled) return undefined;
      return html5QrCode.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 240, height: 240 } },
        (decodedText) => {
          onScannedRef.current(decodedText);
        },
        () => {
          // Callback lỗi decode từng frame — bỏ qua, đây là tín hiệu bình thường khi chưa thấy mã QR nào
        }
      );
    });

    startPromise
      .then(() => {
        if (!cancelled) setStatus('scanning');
      })
      .catch((err) => {
        if (cancelled) return;
        setStatus('error');
        const message = String(err?.message || err || '');
        if (message.toLowerCase().includes('permission') || message.toLowerCase().includes('notallowed')) {
          setErrorMessage(st('trinh_duyet_chua_duoc_cap_quyen_text'));
        } else if (message.toLowerCase().includes('notfound')) {
          setErrorMessage(st('khong_tim_thay_camera_tren_thiet_text'));
        } else {
          setErrorMessage(st('khong_the_khoi_dong_camera_ban_text'));
        }
      });

    return () => {
      cancelled = true;
      cameraReleasedRef.current = stopHtml5QrcodeSafely(html5QrCode, startPromise);
      setStatus('idle');
    };
  }, [isShown, source]);

  // Ngắt cổng COM khi đóng modal hoặc chuyển sang nguồn khác — trả cổng cho nơi khác dùng.
  useEffect(() => {
    if ((!isShown || source !== 'serial') && serialScanner.status === 'connected') {
      void serialScanner.disconnect();
    }
    // serialScanner đổi tham chiếu mỗi lần render — chỉ phụ thuộc .status và .disconnect (ổn định).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isShown, source, serialScanner.status, serialScanner.disconnect]);

  const handleOpened = () => {
    setManualLink('');
    setManualError('');
    setImageError('');
    setErrorMessage('');
    setIsShown(true);
  };

  const handleClosed = () => {
    setIsShown(false);
  };

  const handleSourceChange = (next: ScanSource) => {
    setErrorMessage('');
    setImageError('');
    setSource(next);
  };

  const handleImageChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setImageError('');
    setIsDecodingImage(true);
    try {
      const reader = new Html5Qrcode(FILE_READER_ELEMENT_ID, { verbose: false } as any);
      const decoded = await reader.scanFile(file, false);
      if (decoded?.trim()) {
        onScannedRef.current(decoded.trim());
      } else {
        setImageError(st('khong_doc_duoc_ma_qr_tu_anh_text'));
      }
    } catch {
      setImageError(st('khong_doc_duoc_ma_qr_tu_anh_text'));
    } finally {
      setIsDecodingImage(false);
    }
  };

  const handleManualSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!manualLink.trim()) {
      setManualError(st('vui_long_dan_link_tra_cuu_text'));
      return;
    }
    setManualError('');
    onScanned(manualLink.trim());
  };

  const sourceOptions: { value: ScanSource; icon: string; label: string }[] = [
    { value: 'camera', icon: 'ri-camera-line', label: st('nguon_camera_option') },
    { value: 'serial', icon: 'ri-plug-line', label: st('nguon_cong_com_option') },
    { value: 'image', icon: 'ri-image-line', label: st('nguon_anh_ve_option') },
  ];
  const currentSource = sourceOptions.find((opt) => opt.value === source) ?? sourceOptions[0];

  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      onOpened={handleOpened}
      onClosed={handleClosed}
      centered
      contentClassName="qr-scanner-modal"
    >
      <ModalHeader toggle={onClose}>
        <i className="ri-qr-scan-2-line align-bottom me-2"></i>
        {st('quet_ma_qr_tren_ve_title')}
      </ModalHeader>
      <ModalBody>
        {/* Chọn nguồn đọc mã QR — nút dropdown, chọn lại nguồn khác thì khung bên dưới đổi theo. */}
        <div className="d-flex align-items-center justify-content-between mb-3">
          <span className="text-muted small">{st('chon_nguon_quet_text')}</span>
          <Dropdown isOpen={isSourceMenuOpen} toggle={() => setIsSourceMenuOpen((v) => !v)}>
            <DropdownToggle color="primary" outline size="sm" caret>
              <i className={`${currentSource.icon} align-bottom me-1`} />
              {currentSource.label}
            </DropdownToggle>
            <DropdownMenu end>
              {sourceOptions.map((opt) => (
                <DropdownItem
                  key={opt.value}
                  active={source === opt.value}
                  onClick={() => handleSourceChange(opt.value)}
                >
                  <i className={`${opt.icon} align-bottom me-2`} />
                  {opt.label}
                </DropdownItem>
              ))}
            </DropdownMenu>
          </Dropdown>
        </div>

        {/* Element giải mã ảnh — html5-qrcode bắt buộc có 1 element trong DOM, ẩn đi. */}
        <div id={FILE_READER_ELEMENT_ID} className="d-none" />

        {source === 'camera' && (
          <>
            <div className="qr-scanner-viewport">
              <div id={SCANNER_ELEMENT_ID} className="qr-scanner-region" />
              {status === 'starting' && (
                <div className="qr-scanner-overlay">
                  <div className="spinner-border text-light" role="status">
                    <span className="visually-hidden">{st('dang_mo_camera_text')}</span>
                  </div>
                  <p className="mt-2 mb-0 text-light">{st('dang_mo_camera_text')}</p>
                </div>
              )}
            </div>

            {status === 'scanning' && (
              <p className="text-muted text-center small mt-3 mb-0">
                {st('dua_ma_qr_tren_ve_vao_text')}
              </p>
            )}

            {status === 'error' && (
              <div className="mt-3">
                <div className="alert alert-warning py-2 mb-3">
                  <i className="ri-error-warning-line me-1"></i>
                  {errorMessage}
                </div>
                <form onSubmit={handleManualSubmit}>
                  <Input
                    type="text"
                    placeholder={st('dan_link_tra_cuu_in_tren_placeholder')}
                    value={manualLink}
                    onChange={(e) => setManualLink(e.target.value)}
                    invalid={!!manualError}
                  />
                  {manualError && <FormFeedback>{manualError}</FormFeedback>}
                  <Button color="primary" size="sm" type="submit" className="w-100 mt-2">
                    {st('tra_cuu_button')}
                  </Button>
                </form>
              </div>
            )}
          </>
        )}

        {source === 'serial' && (
          <div className="border rounded p-3">
            {!serialScanner.supported ? (
              <p className="text-danger small mb-0">
                <i className="ri-error-warning-line me-1" />
                {st('trinh_duyet_khong_ho_tro_cong_com_text')}
              </p>
            ) : serialScanner.status === 'connected' ? (
              <>
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <span className="badge bg-success-subtle text-success text-truncate">
                    <i className="ri-plug-line me-1" />
                    {serialScanner.portLabel} · {serialBaud} baud
                  </span>
                  <Button color="danger" outline size="sm" onClick={() => serialScanner.disconnect()}>
                    {st('ngat_cong_com_button')}
                  </Button>
                </div>
                <p className="text-muted small mt-2 mb-0">
                  {st('bop_co_may_quet_vao_ma_qr_text')}
                </p>
                {serialScanner.rxBytes > 0 && serialScanner.rxCount === 0 && (
                  <p className="text-warning small mt-1 mb-0">
                    <i className="ri-error-warning-line me-1" />
                    {st('co_du_lieu_nhung_khong_doc_duoc_text')}
                  </p>
                )}
              </>
            ) : (
              <>
                <Label className="form-label small mb-1">{st('toc_do_baud_label')}</Label>
                <Input
                  type="select"
                  bsSize="sm"
                  className="mb-2"
                  value={serialBaud}
                  onChange={(e) => setSerialBaud(Number(e.target.value))}
                >
                  {SERIAL_BAUD_OPTIONS.map((b) => (
                    <option key={b} value={b}>{b} baud</option>
                  ))}
                </Input>
                <Button
                  color="primary"
                  size="sm"
                  className="w-100"
                  disabled={serialScanner.status === 'connecting'}
                  onClick={() => serialScanner.connect()}
                >
                  {serialScanner.status === 'connecting' ? (
                    <Spinner size="sm" />
                  ) : (
                    <>
                      <i className="ri-plug-line align-bottom me-1" />
                      {st('ket_noi_cong_com_button')}
                    </>
                  )}
                </Button>
                <p className="text-muted small mt-2 mb-0">{st('chon_cong_com_cua_may_quet_text')}</p>
              </>
            )}
            {errorMessage && (
              <div className="alert alert-warning py-2 mt-2 mb-0 small">
                <i className="ri-error-warning-line me-1"></i>
                {errorMessage}
              </div>
            )}
          </div>
        )}

        {source === 'image' && (
          <div className="border rounded p-3 text-center">
            <Label className="btn btn-outline-primary w-100 mb-2">
              {isDecodingImage ? (
                <><Spinner size="sm" className="me-1" />{st('dang_doc_ma_qr_text')}</>
              ) : (
                <><i className="ri-image-add-line align-bottom me-1" />{st('chon_chup_anh_ve_button')}</>
              )}
              <input
                type="file"
                accept="image/*"
                capture="environment"
                hidden
                disabled={isDecodingImage}
                onChange={handleImageChange}
              />
            </Label>
            <p className="text-muted small mb-0">{st('chon_anh_co_ma_qr_ro_net_text')}</p>
            {imageError && (
              <div className="alert alert-warning py-2 mt-2 mb-0 small text-start">
                <i className="ri-error-warning-line me-1"></i>
                {imageError}
              </div>
            )}
          </div>
        )}
      </ModalBody>
    </Modal>
  );
};

export default QrScannerModal;
